import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, RotateCcw, ShieldCheck, Loader2, CheckCircle2, AlertTriangle, Clock, Boxes, FlaskConical, Rocket, Target } from "lucide-react";
import Header from "@/components/Header";
import { useLang } from "@/i18n";
import api, { apiError } from "@/lib/api";

function Stat({ label, value, color, Icon }) {
  return (
    <div className="card-surface rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-mono uppercase tracking-wider text-gray-600">{label}</span>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
      <div className={`font-display text-3xl font-bold ${color}`}>{value}</div>
    </div>
  );
}

export default function AgentScript() {
  const { lang } = useLang();
  const de = lang === "de";
  const [stable, setStable] = useState({ version: 0, script: "" });
  const [draft, setDraft] = useState({ version: 0, script: "" });
  const [codes, setCodes] = useState("");
  const [groups, setGroups] = useState("");
  const [canRollback, setCanRollback] = useState(false);
  const [dirtyStable, setDirtyStable] = useState(false);
  const [dirtyDraft, setDirtyDraft] = useState(false);
  const [rollout, setRollout] = useState({ stable_version: 0, draft_version: 0, total_agents: 0, on_stable: 0, on_draft: 0, outdated: 0, errors: 0, error_devices: [] });
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([api.get("/agent/script"), api.get("/agent/rollout")]);
      setStable(s.data.stable || { version: 0, script: "" });
      setDraft(s.data.draft && s.data.draft.script ? s.data.draft : { version: s.data.draft?.version || 0, script: (s.data.stable || {}).script || "" });
      setCodes(((s.data.test_targets || {}).device_codes || []).join(", "));
      setGroups(((s.data.test_targets || {}).groups || []).join(", "));
      setCanRollback(s.data.can_rollback);
      setDirtyStable(false);
      setDirtyDraft(false);
      setRollout(r.data);
    } catch (err) { toast.error(apiError(err)); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const iv = setInterval(() => { api.get("/agent/rollout").then((r) => setRollout(r.data)).catch(() => {}); }, 15000);
    return () => clearInterval(iv);
  }, []);

  const split = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

  const saveDraft = async () => {
    if (!draft.script.trim()) { toast.error(de ? "Entwurf darf nicht leer sein" : "Draft cannot be empty"); return; }
    setBusy("draft");
    try {
      await api.post("/agent/script/draft", { script: draft.script });
      await api.post("/agent/script/targets", { device_codes: split(codes), groups: split(groups) });
      toast.success(de ? "Entwurf gespeichert & Testgeräte zugewiesen" : "Draft saved & test devices assigned");
      await load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };

  const saveTargets = async () => {
    setBusy("targets");
    try {
      await api.post("/agent/script/targets", { device_codes: split(codes), groups: split(groups) });
      toast.success(de ? "Testgeräte gespeichert" : "Test devices saved");
      await load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };

  const promote = async () => {
    if (!window.confirm(de ? "Den Entwurf für ALLE Geräte freigeben? Er wird die neue stabile Version." : "Release the draft to ALL devices? It becomes the new stable version.")) return;
    setBusy("promote");
    try {
      const res = await api.post("/agent/script/promote");
      toast.success(`${de ? "Für alle freigegeben – stabile Version" : "Released to all – stable version"} v${res.data.version}`);
      await load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };

  const saveStable = async () => {
    if (!stable.script.trim()) { toast.error(de ? "Skript darf nicht leer sein" : "Script cannot be empty"); return; }
    setBusy("stable");
    try {
      const res = await api.post("/agent/script/stable", { script: stable.script });
      toast.success(`${de ? "Stabil gespeichert – Version" : "Stable saved – version"} v${res.data.version}`);
      await load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };

  const rollback = async () => {
    if (!window.confirm(de ? "Stabile Version auf die vorherige zurücksetzen? Alle Geräte übernehmen sie automatisch." : "Roll back stable to the previous version? All devices pick it up automatically.")) return;
    setBusy("rollback");
    try {
      const res = await api.post("/agent/script/rollback");
      toast.success(`${de ? "Zurückgesetzt – Version" : "Rolled back – version"} v${res.data.version}`);
      await load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };

  const taCls = "w-full h-[340px] bg-white border border-gray-200 rounded-xl p-4 text-xs font-mono text-gray-800 leading-relaxed focus:ring-1 outline-none resize-y";
  const inCls = "w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono text-gray-900 focus:border-[#c00000] focus:ring-1 focus:ring-[#c00000] outline-none";

  return (
    <div className="min-h-screen">
      <Header />
      <main className="w-full px-4 sm:px-6 py-6 space-y-5">
        <div className="fade-up">
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{de ? "Agent-Skript & gestaffeltes Ausrollen" : "Agent script & staged rollout"}</h1>
          <p className="text-sm text-gray-600 mt-2 max-w-3xl">
            {de
              ? "Zwei Kanäle: „Stabil“ läuft auf allen Geräten, „Entwurf“ nur auf zugewiesenen Testgeräten. So erweiterst du den Agent Schritt für Schritt: Entwurf testen → wenn ok → „Für alle freigeben“."
              : "Two channels: \"Stable\" runs on all devices, \"Draft\" only on assigned test devices. Extend the agent step by step: test the draft → if OK → \"Release to all\"."}
          </p>
        </div>

        {/* Rollout status */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4" data-testid="rollout-stats">
          <Stat label={de ? "Stabil" : "Stable"} value={`v${rollout.stable_version}`} color="text-[#c00000]" Icon={CheckCircle2} />
          <Stat label={de ? "Entwurf" : "Draft"} value={rollout.draft_version ? `v${rollout.draft_version}` : "—"} color="text-amber-600" Icon={FlaskConical} />
          <Stat label={de ? "Auf Stabil" : "On stable"} value={rollout.on_stable} color="text-gray-900" Icon={Boxes} />
          <Stat label={de ? "Auf Entwurf (Test)" : "On draft (test)"} value={rollout.on_draft} color="text-amber-700" Icon={FlaskConical} />
          <Stat label={de ? "Fehler" : "Errors"} value={rollout.errors} color="text-red-600" Icon={AlertTriangle} />
        </div>

        {/* Safety note */}
        <div className="card-surface rounded-xl p-4 flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-[#c00000] shrink-0 mt-0.5" />
          <div className="text-sm text-gray-700 leading-relaxed">
            <span className="font-semibold text-gray-800">{de ? "Sicherheitsnetz:" : "Safety net:"}</span>{" "}
            {de
              ? "Jedes Skript wird per SHA256 geprüft und isoliert ausgeführt. Ein Fehler kann den Agent nicht abstürzen lassen – er fällt auf die letzte funktionierende Version zurück und meldet den Fehler. So testest du gefahrlos zuerst auf wenigen Geräten."
              : "Every script is SHA256-verified and run in isolation. An error cannot crash the agent – it falls back to the last working version and reports the error. Test safely on a few devices first."}
          </div>
        </div>

        {/* Error devices */}
        {rollout.error_devices && rollout.error_devices.length > 0 && (
          <div className="card-surface rounded-xl p-4" data-testid="error-devices">
            <div className="flex items-center gap-2 text-sm font-semibold text-red-700 mb-3">
              <AlertTriangle className="w-4 h-4" /> {de ? "Geräte mit Update-Fehler" : "Devices with update error"} ({rollout.error_devices.length})
            </div>
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {rollout.error_devices.map((d) => (
                <div key={d.device_code} className="flex items-center gap-3 text-sm font-mono">
                  <span className="text-[#c00000] w-28 shrink-0">{d.device_code}</span>
                  <span className="text-gray-600">{d.channel === "draft" ? "Entwurf" : "Stabil"} v{d.payload_version ?? "?"}</span>
                  <span className="text-red-600 truncate">{d.payload_error || "—"}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
          {/* DRAFT / TEST */}
          <div className="card-surface rounded-xl p-5 space-y-3 border border-amber-500/20">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <FlaskConical className="w-4 h-4 text-amber-600" />
                <span className="font-semibold text-amber-700">{de ? "Entwurf / Test" : "Draft / test"}</span>
                <span className="text-xs font-mono text-gray-600">v{draft.version}</span>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="save-draft" onClick={saveDraft} disabled={busy === "draft"}
                  className="flex items-center gap-2 bg-amber-500/20 border border-amber-500/40 text-amber-200 rounded-lg px-3 py-2 text-sm hover:bg-amber-500/30 disabled:opacity-50 transition-colors">
                  {busy === "draft" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {de ? "Entwurf speichern" : "Save draft"}
                </button>
                <button data-testid="promote-draft" onClick={promote} disabled={busy === "promote"}
                  className="flex items-center gap-2 bg-[#c00000] hover:bg-[#c00000] text-white font-semibold rounded-lg px-3 py-2 text-sm disabled:opacity-50 transition-colors">
                  {busy === "promote" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} {de ? "Für alle freigeben" : "Release to all"}
                </button>
              </div>
            </div>
            <textarea data-testid="draft-editor" value={draft.script} spellCheck={false}
              onChange={(e) => { setDraft((d) => ({ ...d, script: e.target.value })); setDirtyDraft(true); }}
              className={`${taCls} focus:border-amber-500`} />
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-gray-600">
                <Target className="w-3.5 h-3.5" /> {de ? "Testgeräte für den Entwurf" : "Test devices for the draft"}
              </div>
              <div>
                <label className="text-[11px] text-gray-600 mb-1 block">{de ? "Geräte-Codes (Komma-getrennt)" : "Device codes (comma separated)"}</label>
                <input data-testid="targets-codes" value={codes} onChange={(e) => setCodes(e.target.value)} placeholder="BERC03-01, FRAT03-01" className={inCls} />
              </div>
              <div>
                <label className="text-[11px] text-gray-600 mb-1 block">{de ? "Gruppen (Komma-getrennt)" : "Groups (comma separated)"}</label>
                <input data-testid="targets-groups" value={groups} onChange={(e) => setGroups(e.target.value)} placeholder="Testgruppe, BERLIN" className={inCls} />
              </div>
              <button data-testid="save-targets" onClick={saveTargets} disabled={busy === "targets"}
                className="text-sm text-gray-700 border border-gray-200 bg-gray-50 rounded-lg px-3 py-1.5 hover:bg-gray-100 disabled:opacity-50 transition-colors">
                {de ? "Nur Testgeräte speichern" : "Save test devices only"}
              </button>
            </div>
            <p className="text-xs text-gray-600">
              {de ? "Nur diese Geräte/Gruppen laden den Entwurf. Alle anderen bleiben auf der stabilen Version." : "Only these devices/groups load the draft. Everyone else stays on the stable version."}
            </p>
          </div>

          {/* STABLE */}
          <div className="card-surface rounded-xl p-5 space-y-3 border border-[#c00000]/20">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#c00000]" />
                <span className="font-semibold text-[#c00000]">{de ? "Stabil (alle Geräte)" : "Stable (all devices)"}</span>
                <span className="text-xs font-mono text-gray-600" data-testid="stable-version">v{stable.version}</span>
              </div>
              <div className="flex items-center gap-2">
                {canRollback && (
                  <button data-testid="rollback-script" onClick={rollback} disabled={busy === "rollback"}
                    className="flex items-center gap-2 bg-gray-50 border border-gray-200 text-gray-800 rounded-lg px-3 py-2 text-sm hover:bg-gray-100 disabled:opacity-50 transition-colors">
                    <RotateCcw className="w-4 h-4" /> {de ? "Rollback" : "Rollback"}
                  </button>
                )}
                <button data-testid="save-stable" onClick={saveStable} disabled={busy === "stable" || !dirtyStable}
                  className="flex items-center gap-2 bg-[#c00000] hover:bg-[#c00000] disabled:opacity-50 text-white font-semibold rounded-lg px-3 py-2 text-sm transition-colors">
                  {busy === "stable" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {de ? "Speichern & Ausrollen" : "Save & roll out"}
                </button>
              </div>
            </div>
            <textarea data-testid="stable-editor" value={stable.script} spellCheck={false}
              onChange={(e) => { setStable((s) => ({ ...s, script: e.target.value })); setDirtyStable(true); }}
              className={`${taCls} focus:border-[#c00000]`} />
            <p className="text-xs text-gray-600">
              {de
                ? "PowerShell. Muss am Ende eine Hashtable $Extra zurückgeben – jeder Schlüssel wird als Gerätefeld gespeichert (Spalten/Detail/CSV)."
                : "PowerShell. Must return a $Extra hashtable at the end – each key becomes an extra device field (columns/detail/CSV)."}
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
